import React from 'react';
import {
  CalendarDaysIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  DocumentChartBarIcon,
} from '@heroicons/react/24/outline';
import { Button } from 'components/ui';
import { toast } from 'sonner';
import {
  formatAmount,
  formatDate,
  formatTime,
  getCurrencySymbol,
  getStatusIcon,
  getStatusColor,
  getStatusLabel,
  getGatewayIcon,
} from 'utils/paymentFormatters.jsx';
import { PaymentMetaInfo } from './PaymentMetaInfo';

export const PaymentLinkCard = React.memo(({
  transaction,
  isMobile,
  isCancelling,
  isSyncing,
  onCancel,
  onSync,
  onDownloadInvoice,
  isDownloadingInvoice = false,
  isStudent = false,
  isFeePayment = false,
}) => {
  const payments = Array.isArray(transaction?.Payments) ? transaction.Payments : [];
  const primaryPayment = payments.length > 0 ? payments[0] : null;
  const transactionId = primaryPayment?.CfPaymentId;
  const bankReferenceId = primaryPayment?.BankReference;
  const paymentMethod = primaryPayment?.PaymentGroup || primaryPayment?.PaymentMethod;
  const shouldShowPaymentMeta =
    transaction.LinkStatus === 'PAID' && (transactionId || bankReferenceId || paymentMethod);

  const handleCopyLink = () => {
    if (transaction.LinkUrl) {
      navigator.clipboard.writeText(transaction.LinkUrl);
      toast.success('Link copied to clipboard');
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition-all hover:shadow-md dark:border-gray-700 dark:bg-gray-800 sm:p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-4">
        <div className="flex flex-1 gap-2.5 sm:gap-3">
          <div
            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-base font-semibold sm:h-12 sm:w-12 sm:rounded-xl sm:text-lg ${transaction.LinkStatus === 'ACTIVE'
              ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/25'
              : transaction.LinkStatus === 'CANCELLED'
                ? 'bg-rose-100 text-rose-600 dark:bg-rose-900/25'
                : 'bg-amber-100 text-amber-600 dark:bg-amber-900/25'
              }`}
          >
            {getCurrencySymbol(transaction.Currency)}
          </div>

          <div className="flex-1 min-w-0 space-y-2 sm:space-y-3">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <span className="text-base font-semibold text-gray-900 dark:text-gray-100 sm:text-lg">
                {formatAmount(transaction.Amount, transaction.Currency || 'INR')}
              </span>
              <div
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset sm:px-2.5 sm:py-1 ${getStatusColor(transaction.LinkStatus)}`}
              >
                {getStatusIcon(transaction.LinkStatus)}
                {getStatusLabel(transaction.LinkStatus)}
              </div>

              {/* Payment Type Badge - Only shown in Fee Payment Tab */}
              {isFeePayment && (
                <div
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-tight ring-1 ring-inset sm:px-2.5 sm:py-1 ${transaction.IsPartialPaymentMade
                      ? "bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-900/30 dark:text-blue-400"
                      : "bg-gray-50 text-gray-700 ring-gray-600/20 dark:bg-gray-800 dark:text-gray-400"
                    }`}
                >
                  {transaction.IsPartialPaymentMade ? 'Partial' : 'Full'} Payment
                </div>
              )}
            </div>

            {/* Consolidated Fee Info Row - Neutral Style */}
            {(transaction.IsDiscountApplied || transaction.IsPartialPaymentMade) && (
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-[10px] font-bold text-slate-500 uppercase tracking-widest px-0.5">
                {transaction.IsDiscountApplied && transaction.DiscountValue > 0 && (
                  <span className="flex items-center gap-1">
                    <span className="h-1 w-1 rounded-full bg-slate-300"></span>
                    Discount: {(() => {
                      const isPercent = transaction.IsDiscountTypePercentage === true || transaction.IsDiscountTypePercentage === 'true' || transaction.IsDiscountTypePercentage === 1 || transaction.IsDiscountTypePercentage === '1';
                      return isPercent ? `${transaction.DiscountValue}%` : formatAmount(transaction.DiscountValue, transaction.Currency || 'INR');
                    })()}
                  </span>
                )}
                
                {transaction.IsDiscountApplied && transaction.IsPartialPaymentMade && (
                  <span className="h-2.5 w-[1px] bg-slate-200 dark:bg-slate-700 hidden sm:block"></span>
                )}

                {transaction.IsPartialPaymentMade && (
                  <span className="flex items-center gap-1">
                    <span className="h-1 w-1 rounded-full bg-slate-300"></span>
                    Pending: {(() => {
                      const courseFee = parseFloat(transaction.CourseFee || 0);
                      const hostelFee = parseFloat(transaction.HostelFee || 0);
                      const ptuFee = parseFloat(transaction.PTUCounselingFee || 0);
                      const kitFee = parseFloat(transaction.KitFee || transaction.kitFee || 0);
                      const discountValue = parseFloat(transaction.DiscountValue || 0);
                      const isDiscountApplied = transaction.IsDiscountApplied;
                      const isPercent = transaction.IsDiscountTypePercentage === true || transaction.IsDiscountTypePercentage === 'true' || transaction.IsDiscountTypePercentage === 1 || transaction.IsDiscountTypePercentage === '1';
                      let discountAmount = 0;
                      if (isDiscountApplied) {
                        discountAmount = isPercent ? (courseFee * discountValue) / 100 : discountValue;
                      }
                      const currentPaid = parseFloat(transaction.Amount || 0);
                      const pending = (courseFee + ptuFee + kitFee - discountAmount + hostelFee) - currentPaid;
                      return formatAmount(Math.max(0, pending), transaction.Currency || 'INR');
                    })()}
                  </span>
                )}
              </div>
            )}

            <div className="grid gap-1.5 text-xs sm:gap-2 sm:text-sm">
              <div>
                <div className="flex flex-wrap items-baseline gap-2">
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {transaction.FullName}
                  </p>
                  {transaction.CourseCode && (
                    <span className="text-[10px] font-bold text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 px-1.5 py-0.5 rounded tracking-wider">
                      {(() => {
                        const courseCode = transaction.CourseCode;
                        const fullName = transaction.FullName;
                        const fatherName = transaction.FatherName;
                        const gender = transaction.Gender?.trim()?.toLowerCase();

                        if (!fatherName) return `Admission Code : ${courseCode}-${fullName}`;

                        let relationship = 'C/O';
                        if (gender === 'male') relationship = 'S/O';
                        else if (gender === 'female') relationship = 'D/O';

                        return `Admission Code : ${courseCode}-${fullName} ${relationship} ${fatherName}`;
                      })()}
                    </span>
                  )}
                </div>
                <p className="break-words text-xs text-gray-500 dark:text-gray-400">
                  {transaction.Email}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center gap-1 rounded-md bg-gray-50 px-1.5 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-600/20 dark:bg-gray-700 dark:text-gray-300">
                  {getGatewayIcon(transaction.GatewayName)}
                  {transaction.GatewayName}
                </span>
              </div>
            </div>

            {transaction.Description && !isFeePayment && (
              <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-2 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-700/40 dark:text-gray-300 sm:p-3 sm:text-sm">
                {transaction.Description}
              </div>
            )}

            {shouldShowPaymentMeta && (
              <PaymentMetaInfo
                transactionId={transactionId}
                bankReferenceId={bankReferenceId}
                paymentMethod={paymentMethod}
                isMobile={isMobile}
              />
            )}

            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400 sm:gap-3">
              <span className="inline-flex items-center gap-1">
                <CalendarDaysIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                {formatDate(transaction.CreatedDate)} at {formatTime(transaction.CreatedDate)}
              </span>
              {transaction.LinkUrl && (
                <button
                  type="button"
                  className="inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-primary-600 transition-colors hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
                  onClick={handleCopyLink}
                >
                  <ClipboardDocumentIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  Copy link
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-1.5 pt-1.5 sm:gap-2 sm:pt-2 lg:flex-col lg:items-end lg:justify-start lg:pt-0">
          {isFeePayment && transaction.IsEnrollmentFee && transaction.LinkStatus === 'PAID' && (
            <Button
              variant="outlined"
              size="sm"
              onClick={() => onDownloadInvoice(transaction)}
              className="min-w-[6rem] text-xs sm:min-w-[7rem]"
              disabled={isDownloadingInvoice}
            >
              {isDownloadingInvoice ? (
                <span className="animate-pulse">Downloading...</span>
              ) : (
                <>
                  <DocumentChartBarIcon className="h-4 w-4" />
                  <span className="ml-1">Invoice</span>
                </>
              )}
            </Button>
          )}

          {transaction.LinkStatus === 'ACTIVE' && (
            <Button
              variant="outlined"
              size="sm"
              color="error"
              onClick={() => onCancel(transaction)}
              className={`min-w-[5.5rem] text-xs sm:min-w-[6.5rem] ${isStudent ? 'opacity-60 cursor-not-allowed' : ''}`}
              disabled={isCancelling || isStudent}
            >
              {isCancelling ? 'Cancelling...' : 'Cancel'}
            </Button>
          )}
          {transaction.GatewayName !== 'Mannual Payment' && transaction.GatewayName !== 'Manual Payment' && transaction.GatewayCode !== 'MAN' && transaction.LinkId && (
            <Button
              variant="outlined"
              size="sm"
              onClick={() => onSync(transaction)}
              className={`min-w-[6rem] text-xs sm:min-w-[7rem] ${isStudent ? 'opacity-60 cursor-not-allowed' : ''}`}
              disabled={isSyncing || isStudent}
            >
              <ArrowPathIcon
                className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`}
              />
              <span className="ml-1">
                {isSyncing ? 'Refreshing...' : 'Refresh'}
              </span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});

PaymentLinkCard.displayName = 'PaymentLinkCard';
